import React, { FC, useEffect, useState } from 'react';
import { Box, Divider, LinearProgress, Typography, useTheme } from '@mui/material';
import WidgetService from '../../services/WidgetService';
import StarRating from '../StarRating/StarRating';
import { useTranslation } from 'react-i18next';
import { WidgetNoData } from './WidgetNoData';

interface IReviewScoreSplit {
  score: number;
  count: number;
}

const WidgetReviewSummary: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IReviewScoreSplit[]>([]);
  const [total, setTotal] = useState<number>(0);

  useEffect(() => {
    const controller = new AbortController();

    const getReviews = async () => {
      const response = await WidgetService.reviewScoreSplit(setLoading, controller.signal);

      if (response && response.scores) {
        let t = 0;
        const filteredScores = response.scores.filter((s: IReviewScoreSplit) => s.score > 0);
        filteredScores.forEach((s: IReviewScoreSplit) => (t += s.count));
        setTotal(t);
        setData(filteredScores);
      }
    };

    getReviews();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Box>
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <Box>
          {data.length > 0 ? (
            data.map((value: IReviewScoreSplit, index) => {
              return (
                <Box key={index}>
                  <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '1rem',
                    }}
                  >
                    <Box
                      sx={{
                        flexBasis: '40%',
                        textAlign: 'right',
                      }}
                    >
                      <StarRating value={value.score} />
                    </Box>
                    <Box
                      sx={{
                        flexBasis: '50%',
                        textAlign: 'left',
                      }}
                    >
                      <Typography>
                        {value.count}&nbsp;
                        {value.count === 1 ? t('review') : t('reviews')}
                        {total !== 0 && <>&nbsp;({Math.round((value.count / total) * 100)}%)</>}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })
          ) : (
            <>
              <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
              <WidgetNoData />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WidgetReviewSummary;
